import React, { useState } from 'react';
import Navigation from './Navigation';
import Footer from './Footer';
import FAQsHeroSection from './FAQsHeroSection';
import FAQsCategoryTabs from './FAQsCategoryTabs';
import FAQsAccordion from './FAQsAccordion';
import FAQsCTASection from './FAQsCTASection';

const HelpCenterPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('general');
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);

  const categories = [
    { id: 'general', label: 'General' },
    { id: 'companies', label: 'Companies' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'product', label: 'Product' },
  ];

  const faqData: Record<string, Array<{ id: string; question: string; answer: string }>> = {
    general: [
      {
        id: 'g1',
        question: 'What is Master Ji?',
        answer: 'Master Ji is a digital marketplace connecting customers with skilled tailors and fashion houses. We provide tools for measurement storage, order tracking, and business management to make custom tailoring accessible and efficient.',
      },
      {
        id: 'g2',
        question: 'How do I create an account?',
        answer: 'Click the "Get Started" button and fill in your basic information. You can sign up as a customer seeking tailoring services or as a tailor looking to grow your business. The process takes less than 2 minutes.',
      },
      {
        id: 'g3',
        question: 'Is Master Ji available in my area?',
        answer: 'Master Ji is currently available across India and is expanding to other countries. Enter your location on our homepage to see verified tailors in your area.',
      },
      {
        id: 'g4',
        question: 'How do I contact customer support?',
        answer: 'You can reach our support team via email at infotherealmasterji@gmail.com or through the contact form on our website. We typically respond within 24 hours.',
      },
      {
        id: 'g5',
        question: 'What devices can I use Master Ji on?',
        answer: 'Master Ji is available on web browsers (desktop and mobile) and as a mobile app for iOS and Android. Your data syncs across all devices.',
      },
      {
        id: 'g6',
        question: 'Is my personal information secure?',
        answer: 'Yes, we use industry-standard encryption (AES-256) to protect all your data. We never sell your personal information and comply with data protection regulations.',
      },
      {
        id: 'g7',
        question: 'Can I use Master Ji in multiple languages?',
        answer: 'Currently, Master Ji is available in English. We\'re working on adding more languages including Hindi, Punjabi, and other regional languages.',
      },
      {
        id: 'g8',
        question: 'How do I delete my account?',
        answer: 'You can delete your account from the Profile settings page. This will permanently remove all your data including orders, measurements, and messages. This action cannot be undone.',
      },
    ],
    companies: [
      {
        id: 'c1',
        question: 'How do I register my tailoring business?',
        answer: 'Create a business account by selecting "Join as Business" during signup. Complete your profile with photos of your work, your specialties, and service areas. Our team will verify your business within 48 hours.',
      },
      {
        id: 'c2',
        question: 'What tools are available for tailors?',
        answer: 'Our platform provides order management, customer database, measurement storage, analytics dashboard, communication tools, payment processing, inventory management, and appointment scheduling. All designed specifically for tailoring businesses.',
      },
      {
        id: 'c3',
        question: 'How do I get more customers?',
        answer: 'Complete your profile with high-quality photos, encourage reviews from satisfied customers, respond promptly to inquiries, and maintain high ratings. Featured tailors get priority placement in search results.',
      },
      {
        id: 'c4',
        question: 'Can I manage multiple locations?',
        answer: 'Yes, our Premium and Enterprise plans support multi-location management. You can track orders, manage staff, and view analytics across all your business locations from one dashboard.',
      },
      {
        id: 'c5',
        question: 'How do I manage my inventory?',
        answer: 'Premium and Enterprise plan users have access to our inventory management system. You can track materials, supplies, stock levels, and set up low-stock alerts. Free and Basic plans can upgrade to access this feature.',
      },
      {
        id: 'c6',
        question: 'Can I add my team members (karigars) to my account?',
        answer: 'Currently, team member management is in development. Enterprise plan users will have access to multi-user accounts with role-based permissions. Contact us for early access.',
      },
      {
        id: 'c7',
        question: 'How do I process payments from customers?',
        answer: 'Master Ji integrates with Razorpay for secure payment processing. Customers can pay directly through the platform, and funds are transferred to your account according to your payment schedule.',
      },
      {
        id: 'c8',
        question: 'What analytics and reports are available?',
        answer: 'All plans include basic analytics. Premium and Enterprise plans offer advanced analytics including revenue trends, customer insights, order completion rates, and custom reports. You can export data for your records.',
      },
    ],
    pricing: [
      {
        id: 'p1',
        question: 'Is Master Ji free to use?',
        answer: 'Yes! Our Starter plan is completely free and includes basic features like customer management (up to 50 customers), order tracking (up to 10 orders/month), and basic analytics. Paid plans offer additional features for growing businesses.',
      },
      {
        id: 'p2',
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit/debit cards, UPI, net banking, and popular digital wallets through our secure payment partner Razorpay.',
      },
      {
        id: 'p3',
        question: 'Can I change my plan anytime?',
        answer: 'Absolutely! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and billing is prorated automatically. You\'ll receive a credit or charge for the difference.',
      },
      {
        id: 'p4',
        question: 'Do you offer refunds?',
        answer: 'Yes, we offer a 14-day money-back guarantee for all paid plans. If you\'re not satisfied, contact our support team for a full refund, no questions asked.',
      },
      {
        id: 'p5',
        question: 'What happens if I exceed my plan limits?',
        answer: 'You\'ll receive a notification when you\'re approaching your limits. You can either upgrade your plan or wait until the next billing cycle. Some features may be temporarily restricted until you upgrade.',
      },
      {
        id: 'p6',
        question: 'Do you offer annual billing discounts?',
        answer: 'Yes! Annual plans offer a 17% discount compared to monthly billing. For example, Basic plan is ₹999/month or ₹9,999/year (save ₹1,989).',
      },
    ],
    product: [
      {
        id: 'pr1',
        question: 'How secure is my measurement data?',
        answer: 'We use AES-256 encryption to protect all your data. Your measurements are stored securely and only shared with tailors you choose to work with. We never sell your data.',
      },
      {
        id: 'pr2',
        question: 'How does order tracking work?',
        answer: 'Once you place an order, you\'ll receive real-time updates at each stage: confirmation, production, quality check, and delivery. Your tailor can also share progress photos. You can view the complete timeline in your dashboard.',
      },
      {
        id: 'pr3',
        question: 'Can I communicate with my tailor through the app?',
        answer: 'Yes! Our built-in messaging system allows you to discuss requirements, share reference images, ask questions, and schedule fittings—all within the platform. Messages are stored securely.',
      },
      {
        id: 'pr4',
        question: 'What if my order doesn\'t fit properly?',
        answer: 'We stand behind our tailors. If your garment doesn\'t fit as expected, contact the tailor within 7 days. Most offer free alterations. Our support team can help mediate if needed.',
      },
      {
        id: 'pr5',
        question: 'How do I store and share my measurements?',
        answer: 'You can add your body measurements in the Measurements section of your dashboard. Measurements are stored securely and can be shared with specific tailors when placing orders. You can update measurements anytime.',
      },
      {
        id: 'pr6',
        question: 'Can I schedule appointments with tailors?',
        answer: 'Yes! The Appointments feature allows you to book fitting sessions, consultations, and delivery appointments. You can view available time slots and reschedule if needed.',
      },
      {
        id: 'pr7',
        question: 'What file types can I upload?',
        answer: 'You can upload photos (JPG, PNG) for order references and measurement photos. Maximum file size is 10MB per image. We recommend high-quality images for best results.',
      },
      {
        id: 'pr8',
        question: 'How do I track my order history?',
        answer: 'All your orders are stored in the "My Orders" section of your dashboard. You can filter by status, date, or tailor. Completed orders are archived but remain accessible for your records.',
      },
    ],
  };

  // Filter FAQs based on search query
  const currentFaqs = faqData[activeCategory].filter(
    (faq) =>
      searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);
    setExpandedFaq(null);
  };

  const handleToggleFaq = (faqId: string) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  return (
    <div className="min-h-screen bg-[#fafafb]">
      <Navigation />
      
      <FAQsHeroSection
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {/* FAQ Section */}
      <section className="py-12 lg:py-16 bg-[#fafafb]">
        <div className="max-w-container-lg mx-auto px-4 sm:px-6 lg:px-8">
          <FAQsCategoryTabs
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
          />

          <FAQsAccordion
            faqs={currentFaqs}
            expandedFaq={expandedFaq}
            onToggleFaq={handleToggleFaq}
          />
        </div>
      </section>

      <FAQsCTASection />

      <Footer />
    </div>
  );
};

export default HelpCenterPage;
